<template>
  <div class="admin-panel">
    <h1>Admin-Verwaltung</h1>
    
    <!-- Tab-Navigation -->
    <div class="admin-tabs">
      <button 
        :class="{ active: activeTab === 'products' }" 
        @click="activeTab = 'products'"
        class="tab-button"
      >
        Produktverwaltung
      </button>
      <button
        :class="{ active: activeTab === 'tags' }"
        @click="activeTab = 'tags'"
        class="tab-button"
      >
        Tags
      </button>
      <button 
        :class="{ active: activeTab === 'texts' }" 
        @click="activeTab = 'texts'"
        class="tab-button"
      >
        Texte & Farben
      </button>
      <button 
        :class="{ active: activeTab === 'metadata' }" 
        @click="activeTab = 'metadata'"
        class="tab-button"
      >
        Metadaten
      </button>
    </div>

    <!-- Produktverwaltung Tab -->
    <div v-if="activeTab === 'products'" class="tab-content">
      <h2>Produktverwaltung</h2>
      
      <!-- Neues Produkt hinzufügen -->
      <div class="add-product-form">
        <h3>Neues Produkt hinzufügen</h3>
        <form @submit.prevent="addProduct">
          <div class="form-group">
            <label for="productName">Produktname:</label>
            <input v-model="newProduct.name" id="productName" type="text" required />
          </div>
          <div class="form-group">
            <label for="productPrice">Preis (€):</label>
            <input v-model.number="newProduct.price" id="productPrice" type="number" step="0.01" required />
          </div>
          <div class="form-group">
            <label for="productDescription">Beschreibung:</label>
            <textarea v-model="newProduct.description" id="productDescription" rows="4"></textarea>
          </div>
          <div class="form-group">
            <label>Tags zuweisen:</label>
            <div v-if="tags.length" class="tag-select-grid">
              <label v-for="tag in tags" :key="`new-${tag.id}`" class="tag-select-item">
                <input
                  type="checkbox"
                  :value="tag.id"
                  v-model="newProduct.selectedTagIds"
                />
                <span class="tag-preview-pill" :style="{ backgroundColor: tag.color || '#3498db' }">
                  {{ tag.name }}
                </span>
              </label>
            </div>
            <p v-else class="inline-help">Noch keine Tags vorhanden. Lege sie im Tab "Tags" an.</p>
          </div>
          <div class="form-group">
            <label for="productImage">Bild-URL:</label>
            <input v-model="newProduct.image" id="productImage" type="text" placeholder="https://... oder /uploads/..." />
          </div>
          <div class="form-group">
            <label>Bilder hochladen / Reihenfolge</label>
            <input type="file" accept="image/*" @change="handleNewProductUpload" />
            <div class="image-tiles" v-if="newProduct.images && newProduct.images.length">
              <div v-for="(img, idx) in newProduct.images" :key="idx" class="tile"
                   draggable="true"
                   @dragstart="dragStart($event, idx)"
                   @dragend="dragEnd($event)"
                   @dragover.prevent
                   @drop="dropImage($event, idx, newProduct)">
                <img :src="img" class="thumb" />
                <div class="tile-actions">
                  <button type="button" @click="moveImage(newProduct, idx, -1)" class="btn">↑</button>
                  <button type="button" @click="moveImage(newProduct, idx, 1)" class="btn">↓</button>
                  <button type="button" @click="removeImage(newProduct, idx)" class="btn btn-danger">Entf</button>
                </div>
              </div>
            </div>
          </div>
          <button type="submit" class="btn btn-primary">Produkt hinzufügen</button>
        </form>
      </div>

      <!-- Produktliste -->
      <div class="products-list">
        <h3>Aktive Produkte ({{ products.length }})</h3>
        <div v-if="products.length === 0" class="empty-state">Keine Produkte vorhanden</div>
        <div v-else class="products-grid">
          <div v-for="product in products" :key="product.id" class="product-card">
            <img v-if="product.image" :src="product.image" :alt="product.name" class="product-image" />
            <h4>{{ product.name }}</h4>
            <p class="price">{{ product.price }}€</p>
            <p class="description">{{ product.description }}</p>
            <div v-if="product.tags && product.tags.length" class="admin-product-tags">
              <span
                v-for="tag in product.tags"
                :key="`product-${product.id}-tag-${tag.id}`"
                class="tag-preview-pill"
                :style="{ backgroundColor: tag.color || '#3498db' }"
              >
                {{ tag.name }}
              </span>
            </div>
            <div class="product-actions">
              <button @click="editProduct(product)" class="btn btn-secondary">Bearbeiten</button>
              <button @click="deleteProduct(product.id)" class="btn btn-danger">Löschen</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Produkt bearbeiten Modal -->
      <div v-if="editingProduct" class="modal-overlay" @click="editingProduct = null">
        <div class="modal-content" @click.stop>
          <h3>Produkt bearbeiten</h3>
          <form @submit.prevent="saveProduct">
            <div class="form-group">
              <label for="editProductName">Produktname:</label>
              <input v-model="editingProduct.name" id="editProductName" type="text" required />
            </div>
            <div class="form-group">
              <label for="editProductPrice">Preis (€):</label>
              <input v-model.number="editingProduct.price" id="editProductPrice" type="number" step="0.01" required />
            </div>
            <div class="form-group">
              <label for="editProductDescription">Beschreibung:</label>
              <textarea v-model="editingProduct.description" id="editProductDescription" rows="4"></textarea>
            </div>
            <div class="form-group">
              <label>Tags zuweisen:</label>
              <div v-if="tags.length" class="tag-select-grid">
                <label v-for="tag in tags" :key="`edit-${tag.id}`" class="tag-select-item">
                  <input
                    type="checkbox"
                    :value="tag.id"
                    v-model="editingProduct.selectedTagIds"
                  />
                  <span class="tag-preview-pill" :style="{ backgroundColor: tag.color || '#3498db' }">
                    {{ tag.name }}
                  </span>
                </label>
              </div>
              <p v-else class="inline-help">Noch keine Tags vorhanden. Lege sie im Tab "Tags" an.</p>
            </div>
            <div class="form-group">
              <label>Bilder hochladen / Reihenfolge</label>
              <input type="file" accept="image/*" @change="handleEditProductUpload" />
              <div class="image-tiles" v-if="editingProduct.images && editingProduct.images.length">
                <div v-for="(img, idx) in editingProduct.images" :key="idx" class="tile"
                     draggable="true"
                     @dragstart="dragStart($event, idx)"
                     @dragend="dragEnd($event)"
                     @dragover.prevent
                     @drop="dropImage($event, idx, editingProduct)">
                  <img :src="img" class="thumb" />
                  <div class="tile-actions">
                    <button type="button" @click="moveImage(editingProduct, idx, -1)" class="btn">↑</button>
                    <button type="button" @click="moveImage(editingProduct, idx, 1)" class="btn">↓</button>
                    <button type="button" @click="removeImage(editingProduct, idx)" class="btn btn-danger">Entf</button>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-actions">
              <button type="submit" class="btn btn-primary">Speichern</button>
              <button type="button" @click="editingProduct = null" class="btn btn-secondary">Abbrechen</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Tags Tab -->
    <div v-if="activeTab === 'tags'" class="tab-content">
      <h2>Tags verwalten</h2>

      <div class="admin-section">
        <h3>Neuen Tag erstellen</h3>
        <form @submit.prevent="addTag">
          <div class="form-group">
            <label for="newTagName">Name:</label>
            <input v-model="newTag.name" id="newTagName" type="text" required />
          </div>
          <div class="form-group">
            <label for="newTagDescription">Beschreibung (Tooltip):</label>
            <textarea v-model="newTag.description" id="newTagDescription" rows="3"></textarea>
          </div>
          <div class="form-group">
            <label for="newTagColor">Farbe:</label>
            <div class="color-input">
              <input v-model="newTag.color" id="newTagColor" type="color" />
              <span>{{ newTag.color }}</span>
            </div>
          </div>
          <button type="submit" class="btn btn-primary">Tag erstellen</button>
        </form>
      </div>

      <div class="admin-section">
        <h3>Bestehende Tags ({{ tags.length }})</h3>
        <div v-if="!tags.length" class="empty-state">Noch keine Tags vorhanden.</div>
        <div v-else class="admin-tags-list">
          <div v-for="tag in tags" :key="`tag-row-${tag.id}`" class="admin-tag-row">
            <span class="tag-preview-pill" :style="{ backgroundColor: tag.color || '#3498db' }">{{ tag.name }}</span>
            <p class="admin-tag-description">{{ tag.description || 'Keine Beschreibung hinterlegt.' }}</p>
            <div class="admin-tag-actions">
              <button type="button" class="btn btn-secondary" @click="startEditTag(tag)">Bearbeiten</button>
              <button type="button" class="btn btn-danger" @click="deleteTag(tag.id)">Löschen</button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="editingTag" class="modal-overlay" @click="cancelEditTag">
        <div class="modal-content" @click.stop>
          <h3>Tag bearbeiten</h3>
          <form @submit.prevent="saveTagEdit">
            <div class="form-group">
              <label for="editTagName">Name:</label>
              <input v-model="editingTag.name" id="editTagName" type="text" required />
            </div>
            <div class="form-group">
              <label for="editTagDescription">Beschreibung (Tooltip):</label>
              <textarea v-model="editingTag.description" id="editTagDescription" rows="3"></textarea>
            </div>
            <div class="form-group">
              <label for="editTagColor">Farbe:</label>
              <div class="color-input">
                <input v-model="editingTag.color" id="editTagColor" type="color" />
                <span>{{ editingTag.color }}</span>
              </div>
            </div>
            <div class="modal-actions">
              <button type="submit" class="btn btn-primary">Speichern</button>
              <button type="button" class="btn btn-secondary" @click="cancelEditTag">Abbrechen</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Texte & Farben Tab -->
    <div v-if="activeTab === 'texts'" class="tab-content">
      <h2>Texte & Farben verwalten</h2>

      <div class="settings-panel-group">
        <div class="settings-panel">
          <button type="button" class="settings-panel-header" @click="toggleTextColorSection('website')">
            <span>Website</span>
            <span>{{ textColorSections.website ? '–' : '+' }}</span>
          </button>
          <div v-show="textColorSections.website" class="settings-panel-body">
            <RichTextField v-model="richTextValues['site.siteName']" label="Website-Name:" :compact="true" />
            <RichTextField v-model="richTextValues['site.footerText']" label="Footer-Text:" :compact="true" />
          </div>
        </div>

        <div class="settings-panel">
          <button type="button" class="settings-panel-header" @click="toggleTextColorSection('homepage')">
            <span>Startseite</span>
            <span>{{ textColorSections.homepage ? '–' : '+' }}</span>
          </button>
          <div v-show="textColorSections.homepage" class="settings-panel-body">
            <div class="admin-section compact-section">
              <h3>Hero</h3>
              <RichTextField v-model="richTextValues['homepage.heroSection_title']" label="Hero-Titel:" :compact="true" />
              <RichTextField v-model="richTextValues['homepage.heroSection_intro']" label="Hero-Intro:" :allowList="true" hint="Links und E-Mail-Adressen koennen ueber die Buttons 'Link' und 'E-Mail-Link' eingefuegt werden." />
            </div>

            <div class="admin-section compact-section">
              <h3>Kategorien</h3>
              <p class="color-help">Runde Ovale mit Bild, Textfarbe und Produkt-Tag verknüpfen.</p>

              <div class="form-grid compact-grid">
                <div class="form-group">
                  <label for="newHomeCategoryName">Bezeichnung</label>
                  <input v-model="newHomeCategory.name" id="newHomeCategoryName" type="text" placeholder="z. B. Kerzen" />
                </div>

                <div class="form-group">
                  <label for="newHomeCategoryImage">Symbolbild</label>
                  <input v-model="newHomeCategory.image" id="newHomeCategoryImage" type="text" placeholder="https://... oder /uploads/..." />
                  <input type="file" accept="image/*" style="margin-top: 8px" @change="handleHomeCategoryImageUpload" />
                  <img v-if="newHomeCategory.image" :src="newHomeCategory.image" alt="Vorschau Kategorie" class="home-category-preview-image" />
                </div>

                <div class="form-group">
                  <label for="newHomeCategoryTag">Tag</label>
                  <select v-model="newHomeCategory.tagId" id="newHomeCategoryTag">
                    <option value="">Bitte Tag auswählen...</option>
                    <option v-for="tag in tags" :key="tag.id" :value="tag.id">
                      {{ tag.name }}
                    </option>
                  </select>
                </div>

                <div class="form-group">
                  <label for="newHomeCategoryTextColor">Textfarbe</label>
                  <div class="color-input">
                    <input v-model="newHomeCategory.textColor" id="newHomeCategoryTextColor" type="color" />
                    <span>{{ newHomeCategory.textColor }}</span>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label for="newHomeCategoryDescription">Beschreibung</label>
                <textarea v-model="newHomeCategory.description" id="newHomeCategoryDescription" rows="3" placeholder="Beschreibung, die auf der Kategorie-Seite angezeigt wird."></textarea>
              </div>

              <button type="button" class="btn btn-primary" @click="addHomeCategory">Kategorie hinzufügen</button>

              <div v-if="configData.homepage && configData.homepage.categories && configData.homepage.categories.length" class="existing-home-categories" style="margin-top: 1rem;">
                <div v-for="(category, index) in configData.homepage.categories" :key="(category.name || 'category') + '-' + index" class="home-category-item">
                  <div class="home-category-preview" :style="{ color: category.textColor || '#1f1f1f' }">
                    <img v-if="category.image" :src="category.image" :alt="category.name" class="category-icon-small" />
                    <span>{{ category.name }}</span>
                  </div>
                  <div class="home-category-actions">
                    <button type="button" class="btn btn-secondary" @click="startEditHomeCategory(category, index)">Bearbeiten</button>
                    <button type="button" class="btn btn-danger" @click="deleteHomeCategory(index)">Entfernen</button>
                  </div>
                </div>
              </div>

              <div v-if="editingHomeCategory" class="edit-home-category-panel" style="margin-top: 1.25rem;">
                <h4>Kategorie bearbeiten</h4>
                <div class="form-grid compact-grid">
                  <div class="form-group">
                    <label for="editHomeCategoryName">Bezeichnung</label>
                    <input v-model="editingHomeCategory.name" id="editHomeCategoryName" type="text" />
                  </div>

                  <div class="form-group">
                    <label for="editHomeCategoryImage">Symbolbild</label>
                    <input v-model="editingHomeCategory.image" id="editHomeCategoryImage" type="text" placeholder="https://... oder /uploads/..." />
                    <input type="file" accept="image/*" style="margin-top: 8px" @change="handleHomeCategoryEditImageUpload" />
                    <img v-if="editingHomeCategory.image" :src="editingHomeCategory.image" alt="Vorschau Kategorie" class="home-category-preview-image" />
                  </div>

                  <div class="form-group">
                    <label for="editHomeCategoryTag">Tag</label>
                    <select v-model="editingHomeCategory.tagId" id="editHomeCategoryTag">
                      <option value="">Bitte Tag auswählen...</option>
                      <option v-for="tag in tags" :key="tag.id" :value="tag.id">
                        {{ tag.name }}
                      </option>
                    </select>
                  </div>

                  <div class="form-group">
                    <label for="editHomeCategoryTextColor">Textfarbe</label>
                    <div class="color-input">
                      <input v-model="editingHomeCategory.textColor" id="editHomeCategoryTextColor" type="color" />
                      <span>{{ editingHomeCategory.textColor }}</span>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label for="editHomeCategoryDescription">Beschreibung</label>
                  <textarea v-model="editingHomeCategory.description" id="editHomeCategoryDescription" rows="3"></textarea>
                </div>

                <div class="modal-actions">
                  <button type="button" class="btn btn-primary" @click="saveHomeCategoryEdit">Speichern</button>
                  <button type="button" class="btn btn-secondary" @click="cancelEditHomeCategory">Abbrechen</button>
                </div>
              </div>
            </div>

            <div class="admin-section compact-section">
              <h3>Textblock</h3>
              <RichTextField
                v-model="richTextValues['homepage.combinedContent']"
                label="Gesamttext:"
                :allowList="true"
                :allowHeadings="true"
                hint="Tipp: Der Inhalt wird als HTML gespeichert und auf der Startseite direkt angezeigt."
              />
            </div>
          </div>
        </div>

        <div class="settings-panel">
          <button type="button" class="settings-panel-header" @click="toggleTextColorSection('shop')">
            <span>Shop</span>
            <span>{{ textColorSections.shop ? '–' : '+' }}</span>
          </button>
          <div v-show="textColorSections.shop" class="settings-panel-body">
            <div class="admin-section compact-section">
              <h3>Produktseite</h3>
              <RichTextField v-model="richTextValues['productList.welcomeTitle']" label="Willkommens-Überschrift:" :compact="true" />
              <RichTextField v-model="richTextValues['productList.intro']" label="Intro-Text:" :allowList="true" hint="Links oder E-Mail-Adressen hier möglich." />
              <RichTextField v-model="richTextValues['productList.title']" label="Seitentitel:" :compact="true" />
              <RichTextField v-model="richTextValues['productList.loadingMessage']" label="Lade-Nachricht:" :compact="true" />
              <RichTextField v-model="richTextValues['productList.errorMessage']" label="Fehler-Nachricht:" :compact="true" />
            </div>
          </div>
        </div>

        <div class="settings-panel">
          <button type="button" class="settings-panel-header" @click="toggleTextColorSection('pages')">
            <span>Seiten</span>
            <span>{{ textColorSections.pages ? '–' : '+' }}</span>
          </button>
          <div v-show="textColorSections.pages" class="settings-panel-body">
            <div class="admin-section compact-section">
              <h3>Impressum</h3>
              <RichTextField v-model="richTextValues['impressumPage.title']" label="Titel:" :compact="true" />
              <RichTextField v-model="richTextValues['impressumPage.content']" label="Inhalt:" :allowList="true" :allowHeadings="true" hint="Ideal für Links oder Kontaktangaben." />
            </div>
            <div class="admin-section compact-section">
              <h3>Kontakt</h3>
              <RichTextField v-model="richTextValues['contactPage.title']" label="Titel:" :compact="true" />
              <RichTextField v-model="richTextValues['contactPage.content']" label="Inhalt:" :allowList="true" :allowHeadings="true" hint="Dieser Inhalt erscheint auf der Kontakt-Seite und kann mit Links oder Kontaktangaben gepflegt werden." />
            </div>
          </div>
        </div>

        <div class="settings-panel">
          <button type="button" class="settings-panel-header" @click="toggleTextColorSection('buttons')">
            <span>Buttons</span>
            <span>{{ textColorSections.buttons ? '–' : '+' }}</span>
          </button>
          <div v-show="textColorSections.buttons" class="settings-panel-body">
            <div class="admin-section compact-section">
              <h3>Button-Texte</h3>
              <RichTextField v-model="richTextValues['productList.detailsButton']" label="Button-Text:" :compact="true" />
            </div>
          </div>
        </div>

        <div class="settings-panel">
          <button type="button" class="settings-panel-header" @click="toggleTextColorSection('colors')">
            <span>Farben</span>
            <span>{{ textColorSections.colors ? '–' : '+' }}</span>
          </button>
          <div v-show="textColorSections.colors" class="settings-panel-body">
            <div class="admin-section compact-section">
              <p class="color-help">Jede Farbe ist so benannt, dass direkt erkennbar ist, wo sie in der Website erscheint.</p>
              <div class="color-grid">
                <div class="color-section">
                  <h4 class="color-section-title">Hauptfarben</h4>
                  <div class="color-group">
                    <label for="themePrimaryColor">Hauptfarbe</label>
                    <div class="color-input">
                      <input v-model="configData.colors.themePrimaryColor" id="themePrimaryColor" type="color" />
                      <span>{{ configData.colors.themePrimaryColor }}</span>
                    </div>
                  </div>
                  <div class="color-group">
                    <label for="themeOnPrimaryTextColor">Text auf Hauptfarbe</label>
                    <div class="color-input">
                      <input v-model="configData.colors.themeOnPrimaryTextColor" id="themeOnPrimaryTextColor" type="color" />
                      <span>{{ configData.colors.themeOnPrimaryTextColor }}</span>
                    </div>
                  </div>
                  <div class="color-group">
                    <label for="interactiveAccentColor">Akzentfarbe</label>
                    <div class="color-input">
                      <input v-model="configData.colors.interactiveAccentColor" id="interactiveAccentColor" type="color" />
                      <span>{{ configData.colors.interactiveAccentColor }}</span>
                    </div>
                  </div>
                </div>

                <div class="color-section">
                  <h4 class="color-section-title">Schrift</h4>
                  <div class="color-group">
                    <label for="bodyTextColor">Standard-Text</label>
                    <div class="color-input">
                      <input v-model="configData.colors.bodyTextColor" id="bodyTextColor" type="color" />
                      <span>{{ configData.colors.bodyTextColor }}</span>
                    </div>
                  </div>
                  <div class="color-group">
                    <label for="buttonLabelColor">Button-Text</label>
                    <div class="color-input">
                      <input v-model="configData.colors.buttonLabelColor" id="buttonLabelColor" type="color" />
                      <span>{{ configData.colors.buttonLabelColor }}</span>
                    </div>
                  </div>
                </div>

                <div class="color-section">
                  <h4 class="color-section-title">Hintergründe</h4>
                  <div class="color-group">
                    <label for="pageBackgroundColor">Seitenhintergrund</label>
                    <div class="color-input">
                      <input v-model="configData.colors.pageBackgroundColor" id="pageBackgroundColor" type="color" />
                      <span>{{ configData.colors.pageBackgroundColor }}</span>
                    </div>
                  </div>
                  <div class="color-group">
                    <label for="headerBackgroundColor">Header</label>
                    <div class="color-input">
                      <input v-model="configData.colors.headerBackgroundColor" id="headerBackgroundColor" type="color" />
                      <span>{{ configData.colors.headerBackgroundColor }}</span>
                    </div>
                  </div>
                  <div class="color-group">
                    <label for="footerBackgroundColor">Footer</label>
                    <div class="color-input">
                      <input v-model="configData.colors.footerBackgroundColor" id="footerBackgroundColor" type="color" />
                      <span>{{ configData.colors.footerBackgroundColor }}</span>
                    </div>
                  </div>
                  <div class="color-group">
                    <label for="pageBackgroundImage">Hintergrundbild</label>
                    <div class="background-image-controls">
                      <input v-model="configData.colors.pageBackgroundImage" id="pageBackgroundImage" type="text" placeholder="https://... oder /uploads/..." />
                      <input type="file" accept="image/*" @change="handleBackgroundImageUpload" />
                      <button type="button" class="btn btn-secondary" @click="removeBackgroundImage">Bild entfernen</button>
                      <img v-if="configData.colors.pageBackgroundImage" :src="configData.colors.pageBackgroundImage" alt="Seitenhintergrund Vorschau" class="background-preview" />
                    </div>
                  </div>
                </div>

                <div class="color-section">
                  <h4 class="color-section-title">Buttons</h4>
                  <div class="color-group">
                    <label for="buttonBackgroundColor">Button-Hintergrund</label>
                    <div class="color-input">
                      <input v-model="configData.colors.buttonBackgroundColor" id="buttonBackgroundColor" type="color" />
                      <span>{{ configData.colors.buttonBackgroundColor }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="admin-section compact-section save-bar">
        <button @click="saveConfig" class="btn btn-primary btn-large">
          {{ configSaving ? 'Speichern...' : 'Alle Änderungen speichern' }}
        </button>
        <div v-if="configSaveMessage" :class="['message', configSaveMessage.success ? 'success' : 'error']">
          {{ configSaveMessage.text }}
        </div>
      </div>
    </div>

    <!-- Metadaten Tab -->
    <div v-if="activeTab === 'metadata'" class="tab-content">
      <h2>Metadaten & SEO</h2>
      <p class="color-help">Diese Angaben werden als unsichtbare HTML-Meta-Tags in den <code>&lt;head&gt;</code> der Seite geschrieben. Sie sind nicht für Besucher sichtbar, aber wichtig für Suchmaschinen, Browser und Social-Media-Vorschauen.</p>

      <!-- Browser & Allgemein -->
      <div class="admin-section">
        <h3>Browser & Allgemein</h3>

        <div class="form-group">
          <label for="metaPageTitle">Browser-Tab-Titel</label>
          <p class="meta-hint">Erscheint im Browser-Tab und als fettgedruckte Überschrift in Google-Suchergebnissen. Idealerweise 50–60 Zeichen.</p>
          <input v-model="configData.metadata.pageTitle" id="metaPageTitle" type="text" placeholder="z. B. Die kleine Dekoecke" />
        </div>

        <div class="form-group">
          <label for="metaFavicon">Favicon (Browser-Icon)</label>
          <p class="meta-hint">Kleines Symbol im Browser-Tab und in Lesezeichen. Empfohlen: quadratisch, idealerweise 32x32 oder 48x48 px. PNG, ICO oder SVG.</p>
          <input v-model="configData.metadata.favicon" id="metaFavicon" type="text" placeholder="https://... oder /uploads/..." />
          <input type="file" accept="image/*,.ico,.svg" style="margin-top: 8px" @change="handleFaviconUpload" />
          <img v-if="configData.metadata.favicon" :src="configData.metadata.favicon" alt="Favicon Vorschau" class="background-preview" style="margin-top: 8px; max-width: 64px; max-height: 64px;" />
        </div>

        <div class="form-group">
          <label for="metaLang">Sprache der Website</label>
          <p class="meta-hint">Teilt dem Browser und Suchmaschinen mit, in welcher Sprache die Seite verfasst ist. Wird als <code>lang</code>-Attribut im <code>&lt;html&gt;</code>-Tag gesetzt und hilft Screenreadern und Übersetzungstools.</p>
          <select v-model="configData.metadata.lang" id="metaLang">
            <option value="de">Deutsch (de)</option>
            <option value="en">Englisch (en)</option>
            <option value="fr">Französisch (fr)</option>
            <option value="es">Spanisch (es)</option>
          </select>
        </div>

        <div class="form-group">
          <label for="metaThemeColor">Theme-Farbe (Mobilgeräte)</label>
          <p class="meta-hint">Färbt die Adressleiste des Browsers auf Android und iOS ein. Sichtbar z. B. beim Hinzufügen der Seite zum Homescreen. Empfehlung: Hauptfarbe der Website verwenden.</p>
          <div class="color-input">
            <input v-model="configData.metadata.themeColor" id="metaThemeColor" type="color" />
            <span>{{ configData.metadata.themeColor }}</span>
          </div>
        </div>
      </div>

      <!-- SEO -->
      <div class="admin-section">
        <h3>Suchmaschinen (SEO)</h3>

        <div class="form-group">
          <label for="metaDescription">Meta-Description</label>
          <p class="meta-hint">Kurzbeschreibung der Seite, die in Google-Suchergebnissen unter dem Titel erscheint. Hat keinen direkten Einfluss aufs Ranking, aber auf die Klickrate. Empfohlen: 150–160 Zeichen.</p>
          <textarea v-model="configData.metadata.description" id="metaDescription" rows="3" placeholder="z. B. Handgegossene Dekostücke aus Raysin – liebevoll gefertigt in unserer kleinen Werkstatt."></textarea>
        </div>

        <div class="form-group">
          <label for="metaKeywords">Keywords (kommagetrennt)</label>
          <p class="meta-hint">Stichwörter zur Seite. Werden von Google heute kaum noch bewertet, von anderen Suchmaschinen (z. B. Bing) aber noch leicht berücksichtigt.</p>
          <input v-model="configData.metadata.keywords" id="metaKeywords" type="text" placeholder="z. B. Dekoration, Raysin, Handmade, Unikate, Geschenk" />
        </div>

        <div class="form-group">
          <label for="metaAuthor">Autor / Betreiber</label>
          <p class="meta-hint">Name des Seitenbetreibers. Wird von manchen Suchmaschinen und Crawlern ausgewertet und ist ein kleines Vertrauenssignal.</p>
          <input v-model="configData.metadata.author" id="metaAuthor" type="text" placeholder="z. B. Die kleine Dekoecke" />
        </div>

        <div class="form-group">
          <label for="metaCanonicalUrl">Kanonische URL (Canonical)</label>
          <p class="meta-hint">Die „offizielle" Adresse dieser Website. Verhindert Duplicate-Content-Probleme, wenn die Seite unter mehreren URLs erreichbar ist (z. B. mit und ohne www, mit und ohne https). Leer lassen, wenn nicht benötigt.</p>
          <input v-model="configData.metadata.canonicalUrl" id="metaCanonicalUrl" type="text" placeholder="z. B. https://www.die-kleine-dekoecke.de" />
        </div>

        <div class="form-group">
          <label for="metaRobots">Robots-Direktive</label>
          <p class="meta-hint">Steuert, ob Suchmaschinen-Bots die Seite indexieren und enthaltene Links verfolgen dürfen. Normalfall: <em>index, follow</em>. Nur ändern, wenn die Seite nicht öffentlich sein soll.</p>
          <select v-model="configData.metadata.robots" id="metaRobots">
            <option value="index, follow">index, follow – Seite indexieren & Links verfolgen (Standard)</option>
            <option value="noindex, follow">noindex, follow – Seite NICHT indexieren, Links verfolgen</option>
            <option value="index, nofollow">index, nofollow – Seite indexieren, Links NICHT verfolgen</option>
            <option value="noindex, nofollow">noindex, nofollow – Seite komplett aus Suchmaschinen ausschließen</option>
          </select>
        </div>
      </div>

      <!-- Open Graph -->
      <div class="admin-section">
        <h3>Open Graph – Social-Media-Vorschau</h3>
        <p class="color-help">Open Graph (OG) steuert, wie ein Link zur Website auf Facebook, WhatsApp, LinkedIn, Telegram &amp; Co. aussieht, wenn jemand ihn teilt. Ohne diese Angaben wählt die Plattform selbst aus, was angezeigt wird.</p>

        <div class="form-group">
          <label for="metaOgType">Seitentyp (og:type)</label>
          <p class="meta-hint">Art des Inhalts dieser Seite. Für normale Websites immer <em>website</em> wählen.</p>
          <select v-model="configData.metadata.ogType" id="metaOgType">
            <option value="website">website – normale Website (Standard)</option>
            <option value="article">article – Blogartikel oder Newsbeitrag</option>
            <option value="product">product – Produktseite</option>
          </select>
        </div>

        <div class="form-group">
          <label for="metaOgUrl">Seiten-URL (og:url)</label>
          <p class="meta-hint">Die vollständige, öffentliche URL dieser Website. Wird beim Teilen als kanonische Adresse genutzt. Sollte mit der kanonischen URL übereinstimmen.</p>
          <input v-model="configData.metadata.ogUrl" id="metaOgUrl" type="text" placeholder="z. B. https://www.die-kleine-dekoecke.de" />
        </div>

        <div class="form-group">
          <label for="metaOgTitle">Titel der Vorschaukarte (og:title)</label>
          <p class="meta-hint">Überschrift in der Vorschaukarte beim Teilen. Wenn leer, wird der Browser-Tab-Titel verwendet.</p>
          <input v-model="configData.metadata.ogTitle" id="metaOgTitle" type="text" placeholder="z. B. Die kleine Dekoecke – Handgemachte Deko" />
        </div>

        <div class="form-group">
          <label for="metaOgDescription">Beschreibung der Vorschaukarte (og:description)</label>
          <p class="meta-hint">Text unter dem Titel in der Vorschaukarte. Wenn leer, wird die Meta-Description verwendet.</p>
          <textarea v-model="configData.metadata.ogDescription" id="metaOgDescription" rows="3" placeholder="z. B. Handgegossene Dekostücke aus Raysin, liebevoll gefertigt."></textarea>
        </div>

        <div class="form-group">
          <label for="metaOgImage">Vorschaubild (og:image)</label>
          <p class="meta-hint">Bild, das beim Teilen als Vorschau angezeigt wird. Empfohlene Größe: <strong>1200 × 630 px</strong>. Muss eine öffentlich erreichbare URL sein – lokale Uploads funktionieren nur, wenn der Server öffentlich erreichbar ist.</p>
          <input v-model="configData.metadata.ogImage" id="metaOgImage" type="text" placeholder="https://... oder /uploads/..." />
          <input type="file" accept="image/*" style="margin-top: 8px" @change="handleOgImageUpload" />
          <img v-if="configData.metadata.ogImage" :src="configData.metadata.ogImage" alt="OG-Bild Vorschau" class="background-preview" style="margin-top: 8px" />
        </div>
      </div>

      <!-- Twitter/X -->
      <div class="admin-section">
        <h3>Twitter / X – Kartenformat</h3>
        <p class="color-help">Twitter/X nutzt eigene Meta-Tags zusätzlich zu Open Graph. Titel, Beschreibung und Bild werden automatisch von den OG-Feldern oben übernommen – hier wird nur das Kartenformat gesteuert.</p>

        <div class="form-group">
          <label for="metaTwitterCard">Kartenformat (twitter:card)</label>
          <p class="meta-hint"><em>summary_large_image</em> zeigt ein großes Querbild über dem Text – ideal für visuelle Produkte. <em>summary</em> zeigt nur ein kleines Quadrat-Bild neben dem Text.</p>
          <select v-model="configData.metadata.twitterCard" id="metaTwitterCard">
            <option value="summary_large_image">summary_large_image – großes Bild (empfohlen)</option>
            <option value="summary">summary – kleines Bild neben dem Text</option>
          </select>
        </div>
      </div>

      <div class="admin-section">
        <button @click="saveConfig" class="btn btn-primary btn-large">
          {{ configSaving ? 'Speichern...' : 'Alle Änderungen speichern' }}
        </button>
        <div v-if="configSaveMessage" :class="['message', configSaveMessage.success ? 'success' : 'error']">
          {{ configSaveMessage.text }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RichTextField from './RichTextField.vue';
import {
  loadSiteConfig,
  saveSiteConfig,
  verifyAdminByUsername,
  getProducts,
  createProduct,
  updateProductById,
  deleteProductById,
  getTags,
  createTag,
  updateTagById,
  deleteTagById
} from '../services/dbApi';

export default {
  name: 'AdminPanel',
  components: { RichTextField },
  data() {
    return {
      activeTab: 'products',
      products: [],
      tags: [],
      configData: {
        site: { siteName: '', footerText: '' },
        navigation: {},
        colors: {},
        homepage: {
          heroSection_title: '',
          heroSection_intro: '',
          combinedContent: '',
          categories: [],
          whoWeAreSection_title: '',
          whoWeAreSection_text: '',
          whatWeDoSection_title: '',
          whatWeDoSection_text: ''
        },
        productList: {
          welcomeTitle: '',
          intro: '',
          title: '',
          detailsButton: '',
          loadingMessage: '',
          errorMessage: ''
        },
        impressumPage: { title: '', content: '' },
        contactPage: { title: '', content: '' },
        metadata: {
          pageTitle: '',
          favicon: '/favicon.ico',
          description: '',
          keywords: '',
          author: '',
          robots: 'index, follow',
          lang: 'de',
          canonicalUrl: '',
          themeColor: '#d38301',
          ogType: 'website',
          ogUrl: '',
          ogTitle: '',
          ogDescription: '',
          ogImage: '',
          twitterCard: 'summary_large_image'
        }
      },
      configSaving: false,
      configSaveMessage: null,
      newProduct: {
        name: '',
        price: 0,
        description: '',
        image: '',
        images: [],
        selectedTagIds: []
      },
      newTag: {
        name: '',
        description: '',
        color: '#3498db'
      },
      newHomeCategory: {
        name: '',
        image: '',
        tagId: '',
        description: '',
        textColor: '#1f1f1f'
      },
      editingTag: null,
      editingProduct: null,
      editingHomeCategory: null,
      richTextValues: {},
      textColorSections: {
        website: true,
        homepage: true,
        shop: true,
        pages: true,
        buttons: true,
        colors: true
      }
    };
  },
  mounted() {
    this.loadProducts();
    this.loadTags();
    this.loadConfig();
    this.checkAdminAccess();
  },
  methods: {
    normalizeTagIds(rawTagIds) {
      if (!Array.isArray(rawTagIds)) return [];
      const unique = new Set();
      rawTagIds.forEach(rawId => {
        const parsed = Number.parseInt(rawId, 10);
        if (Number.isInteger(parsed) && parsed > 0) {
          unique.add(parsed);
        }
      });
      return Array.from(unique);
    },
    async loadTags() {
      try {
        const tags = await getTags();
        this.tags = Array.isArray(tags) ? tags : [];
      } catch (error) {
        console.error('Fehler beim Laden der Tags:', error);
        this.tags = [];
      }
    },
    async addTag() {
      const trimmedName = (this.newTag.name || '').trim();
      if (!trimmedName) {
        alert('Bitte einen Tag-Namen eingeben.');
        return;
      }

      try {
        const tag = await createTag({
          name: trimmedName,
          description: (this.newTag.description || '').trim(),
          color: this.newTag.color || '#3498db'
        });
        this.tags.push(tag);
        this.tags.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de'));
        this.newTag = { name: '', description: '', color: '#3498db' };
        alert('Tag erfolgreich erstellt!');
      } catch (error) {
        console.error('Fehler beim Erstellen des Tags:', error);
        alert('Fehler beim Erstellen des Tags: ' + (error?.message || 'Unbekannter Fehler'));
      }
    },
    startEditTag(tag) {
      this.editingTag = {
        id: tag.id,
        name: tag.name || '',
        description: tag.description || '',
        color: tag.color || '#3498db'
      };
    },
    cancelEditTag() {
      this.editingTag = null;
    },
    async saveTagEdit() {
      if (!this.editingTag) return;

      const trimmedName = (this.editingTag.name || '').trim();
      if (!trimmedName) {
        alert('Bitte einen Tag-Namen eingeben.');
        return;
      }

      try {
        const updated = await updateTagById(this.editingTag.id, {
          name: trimmedName,
          description: (this.editingTag.description || '').trim(),
          color: this.editingTag.color || '#3498db'
        });

        const index = this.tags.findIndex(tag => tag.id === updated.id);
        if (index !== -1) {
          this.tags[index] = updated;
        }
        this.tags.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de'));
        this.editingTag = null;
        await this.loadProducts();
        alert('Tag erfolgreich aktualisiert!');
      } catch (error) {
        console.error('Fehler beim Aktualisieren des Tags:', error);
        alert('Fehler beim Aktualisieren des Tags: ' + (error?.message || 'Unbekannter Fehler'));
      }
    },
    async deleteTag(tagId) {
      if (!confirm('Möchtest du diesen Tag wirklich löschen?')) return;
      try {
        await deleteTagById(tagId);
        this.tags = this.tags.filter(tag => tag.id !== tagId);
        this.newProduct.selectedTagIds = this.newProduct.selectedTagIds.filter(id => id !== tagId);
        if (this.editingProduct && Array.isArray(this.editingProduct.selectedTagIds)) {
          this.editingProduct.selectedTagIds = this.editingProduct.selectedTagIds.filter(id => id !== tagId);
        }
        await this.loadProducts();
        alert('Tag erfolgreich gelöscht!');
      } catch (error) {
        console.error('Fehler beim Löschen des Tags:', error);
        alert('Fehler beim Löschen des Tags: ' + (error?.message || 'Unbekannter Fehler'));
      }
    },
    normalizeHomepageCategories(rawCategories) {
      if (!Array.isArray(rawCategories)) return [];
      return rawCategories
        .filter(category => category && (category.name || category.image))
        .map(category => ({
          name: String(category.name || '').trim(),
          image: String(category.image || '').trim(),
          tagId: category.tagId !== undefined && category.tagId !== null && category.tagId !== '' ? String(category.tagId) : '',
          description: String(category.description || '').trim(),
          textColor: category.textColor || '#1f1f1f'
        }))
        .filter(category => category.name || category.image);
    },
    async loadConfig() {
      try {
        this.configData = await loadSiteConfig();
        if (!this.configData.homepage) this.configData.homepage = {};
        this.configData.homepage.categories = this.normalizeHomepageCategories(this.configData.homepage.categories);
        this.ensureHomepageCombinedContent();
        this.initializeRichTextValues();
      } catch (err) {
        console.error('Error loading config:', err);
      }
    },
    async saveConfig() {
      this.configSaving = true;
      this.configSaveMessage = null;
      try {
        if (!this.configData.homepage) this.configData.homepage = {};
        this.configData.homepage.categories = this.normalizeHomepageCategories(this.configData.homepage.categories);
        this.applyRichTextValuesToConfig();
        await saveSiteConfig(this.configData);
        this.configSaveMessage = { success: true, text: 'Konfiguration erfolgreich gespeichert!' };
        // Emit event to update other components
        window.dispatchEvent(new Event('config-updated'));
      } catch (err) {
        console.error('Error saving config:', err);
        this.configSaveMessage = { success: false, text: 'Fehler beim Speichern: ' + err.message };
      } finally {
        this.configSaving = false;
      }
    },
    ensureHomepageCombinedContent() {
      if (!this.configData.homepage) this.configData.homepage = {};
      if (typeof this.configData.homepage.combinedContent === 'string' && this.configData.homepage.combinedContent.trim()) {
        return;
      }

      const home = this.configData.homepage;
      const sections = [
        { title: home.whoWeAreSection_title, text: home.whoWeAreSection_text },
        { title: home.whatWeDoSection_title, text: home.whatWeDoSection_text }
      ];

      const html = sections
        .map(section => {
          const safeTitle = (section.title || '').trim();
          const safeText = (section.text || '').trim();
          if (!safeTitle && !safeText) return '';

          let sectionHtml = '<section>';
          if (safeTitle) sectionHtml += `<h2>${safeTitle}</h2>`;
          if (safeText) {
            const paragraphText = safeText
              .split('\n')
              .map(line => line.trim())
              .filter(Boolean)
              .join('<br>');
            if (paragraphText) sectionHtml += `<p>${paragraphText}</p>`;
          }
          sectionHtml += '</section>';
          return sectionHtml;
        })
        .filter(Boolean)
        .join('');

      this.configData.homepage.combinedContent = html;
    },
    getRichTextPaths() {
      return [
        'site.siteName',
        'site.footerText',
        'homepage.heroSection_title',
        'homepage.heroSection_intro',
        'homepage.combinedContent',
        'productList.welcomeTitle',
        'productList.intro',
        'productList.title',
        'productList.detailsButton',
        'productList.loadingMessage',
        'productList.errorMessage',
        'impressumPage.title',
        'impressumPage.content',
        'contactPage.title',
        'contactPage.content'
      ];
    },
    getValueByPath(source, path) {
      return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : ''), source);
    },
    setValueByPath(target, path, value) {
      const parts = path.split('.');
      let current = target;
      for (let index = 0; index < parts.length - 1; index += 1) {
        const key = parts[index];
        if (!current[key] || typeof current[key] !== 'object') {
          current[key] = {};
        }
        current = current[key];
      }
      current[parts[parts.length - 1]] = value;
    },
    toggleTextColorSection(sectionKey) {
      if (!this.textColorSections[sectionKey]) {
        this.textColorSections[sectionKey] = true;
        return;
      }
      this.textColorSections[sectionKey] = !this.textColorSections[sectionKey];
    },
    initializeRichTextValues() {
      const values = {};
      this.getRichTextPaths().forEach(path => {
        values[path] = this.getValueByPath(this.configData, path) || '';
      });
      this.richTextValues = values;
    },
    applyRichTextValuesToConfig() {
      this.getRichTextPaths().forEach(path => {
        const value = this.richTextValues[path] || '';
        this.setValueByPath(this.configData, path, value);
      });
    },
    async checkAdminAccess() {
      // Überprüft mit dem Server, ob der Nutzer Admin-Rechte hat (SICHER!)
      const username = sessionStorage.getItem('username');
      
      if (!username) {
        alert('Du musst angemeldet sein!');
        this.$router.push('/LogIn');
        return;
      }

      try {
        const isAdmin = await verifyAdminByUsername(username);
        
        // Aktualisiere sessionStorage mit Server-Wert
        sessionStorage.setItem('isAdmin', isAdmin ? 'true' : 'false');
        
        if (!isAdmin) {
          alert('Du hast keine Admin-Berechtigung!');
          this.$router.push('/');
        }
      } catch (err) {
        console.error('Fehler bei Admin-Verifizierung:', err);
        alert('Fehler bei der Verifizierung der Admin-Rechte');
        this.$router.push('/');
      }
    },
    async loadProducts() {
      try {
        const data = await getProducts();
        this.products = Array.isArray(data) ? data : [];
        this.products.forEach(p => {
          if (!Array.isArray(p.images)) p.images = p.images ? [p.images] : [];
          p.tags = Array.isArray(p.tags) ? p.tags : [];
        });
      } catch (error) {
        console.error('Fehler beim Laden der Produkte:', error);
        alert('Fehler beim Laden der Produkte aus der Datenbank');
        this.products = [];
      }
    },
    async addProduct() {
      if (!this.newProduct.name || this.newProduct.price <= 0) {
        alert('Bitte fülle alle erforderlichen Felder aus');
        return;
      }

      // Sende Produkt an den Server
      const payload = { ...this.newProduct };
      // ensure images is an array
      payload.images = Array.isArray(payload.images) ? payload.images : (payload.image ? [payload.image] : []);
      payload.tagIds = this.normalizeTagIds(payload.selectedTagIds);
      try {
        const product = await createProduct(payload);
        this.products.push(product);
        this.newProduct = {
          name: '',
          price: 0,
          description: '',
          image: '',
          images: [],
          selectedTagIds: []
        };
        alert('Produkt erfolgreich hinzugefügt!');
      } catch (error) {
        console.error('Fehler beim Hinzufügen des Produkts:', error);
        alert('Fehler beim Hinzufügen des Produkts: ' + (error?.message || 'Unbekannter Fehler'));
      }
    },
    editProduct(product) {
      // clone and ensure images array
      this.editingProduct = {
        ...product,
        images: Array.isArray(product.images) ? [...product.images] : (product.image ? [product.image] : []),
        selectedTagIds: this.normalizeTagIds((product.tags || []).map(tag => tag.id))
      };
    },
    async saveProduct() {
      // Sende aktualisiertes Produkt an den Server
      const payload = { ...this.editingProduct };
      payload.images = Array.isArray(payload.images) ? payload.images : (payload.image ? [payload.image] : []);
      payload.tagIds = this.normalizeTagIds(payload.selectedTagIds);
      try {
        const updated = await updateProductById(this.editingProduct.id, payload);
        const index = this.products.findIndex(p => p.id === this.editingProduct.id);
        if (index !== -1) {
          this.products[index] = updated;
        }
        this.editingProduct = null;
        alert('Produkt erfolgreich aktualisiert!');
      } catch (error) {
        console.error('Fehler beim Aktualisieren des Produkts:', error);
        alert('Fehler beim Aktualisieren des Produkts: ' + (error?.message || 'Unbekannter Fehler'));
      }
    },
    async deleteProduct(productId) {
      if (confirm('Möchtest du dieses Produkt wirklich löschen?')) {
        try {
          await deleteProductById(productId);
          this.products = this.products.filter(p => p.id !== productId);
          alert('Produkt gelöscht!');
        } catch (error) {
          console.error('Fehler beim Löschen des Produkts:', error);
          alert('Fehler beim Löschen des Produkts: ' + (error?.message || 'Unbekannter Fehler'));
        }
      }
    },
    async uploadFile(file) {
      try {
        const maxBytes = 1024 * 1024 * 10;
        if (file.size > maxBytes) {
          throw new Error('Bild ist zu groß. Bitte max. 10 MB verwenden.');
        }

        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch('/api/uploads', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          let message = 'Fehler beim Hochladen des Bildes';
          try {
            const errorData = await response.json();
            message = errorData?.error || errorData?.message || message;
          } catch (_) {
            message = response.statusText || message;
          }
          throw new Error(message);
        }

        const data = await response.json();
        if (!data?.url) {
          throw new Error('Der Server hat keine Bild-URL zurückgegeben.');
        }

        return data.url;
      } catch (err) {
        console.error('UploadFile error', err);
        alert(err?.message || 'Fehler beim Hochladen des Bildes');
        return null;
      }
    },
    async handleNewProductUpload(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const url = await this.uploadFile(file);
      if (url) {
        if (!Array.isArray(this.newProduct.images)) this.newProduct.images = [];
        this.newProduct.images.push(url);
        if (!this.newProduct.image) this.newProduct.image = url;
      }
      e.target.value = '';
    },
    async handleEditProductUpload(e) {
      const file = e.target.files && e.target.files[0];
      if (!file || !this.editingProduct) return;
      const url = await this.uploadFile(file);
      if (url) {
        if (!Array.isArray(this.editingProduct.images)) this.editingProduct.images = [];
        this.editingProduct.images.push(url);
        if (!this.editingProduct.image) this.editingProduct.image = url;
      }
      e.target.value = '';
    },
    async handleOgImageUpload(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const url = await this.uploadFile(file);
      if (url) {
        if (!this.configData.metadata || typeof this.configData.metadata !== 'object') {
          this.configData.metadata = {};
        }
        this.configData.metadata.ogImage = url;
      }
      e.target.value = '';
    },
    async handleFaviconUpload(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const url = await this.uploadFile(file);
      if (url) {
        if (!this.configData.metadata || typeof this.configData.metadata !== 'object') {
          this.configData.metadata = {};
        }
        this.configData.metadata.favicon = url;
      }
      e.target.value = '';
    },
    async handleHomeCategoryImageUpload(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const url = await this.uploadFile(file);
      if (url) {
        this.newHomeCategory.image = url;
      }
      e.target.value = '';
    },
    addHomeCategory() {
      const name = (this.newHomeCategory.name || '').trim();
      if (!name) {
        alert('Bitte einen Kategorienamen eingeben.');
        return;
      }

      if (!this.newHomeCategory.tagId) {
        alert('Bitte ein passendes Produkt-Tag für diese Kategorie auswählen.');
        return;
      }

      if (!this.configData.homepage) this.configData.homepage = {};
      if (!Array.isArray(this.configData.homepage.categories)) this.configData.homepage.categories = [];

      this.configData.homepage.categories.push({
        name,
        image: (this.newHomeCategory.image || '').trim(),
        tagId: String(this.newHomeCategory.tagId),
        description: (this.newHomeCategory.description || '').trim(),
        textColor: this.newHomeCategory.textColor || '#1f1f1f'
      });
      this.newHomeCategory = { name: '', image: '', tagId: '', description: '', textColor: '#1f1f1f' };
    },
    startEditHomeCategory(category, index) {
      this.editingHomeCategory = {
        index,
        name: category.name || '',
        image: category.image || '',
        tagId: category.tagId !== undefined && category.tagId !== null && category.tagId !== '' ? String(category.tagId) : '',
        description: category.description || '',
        textColor: category.textColor || '#1f1f1f'
      };
    },
    cancelEditHomeCategory() {
      this.editingHomeCategory = null;
    },
    saveHomeCategoryEdit() {
      if (!this.editingHomeCategory) return;

      const name = (this.editingHomeCategory.name || '').trim();
      if (!name) {
        alert('Bitte einen Kategorienamen eingeben.');
        return;
      }

      if (!this.editingHomeCategory.tagId) {
        alert('Bitte ein passendes Produkt-Tag für diese Kategorie auswählen.');
        return;
      }

      if (!this.configData.homepage || !Array.isArray(this.configData.homepage.categories)) {
        this.configData.homepage = { categories: [] };
      }

      this.configData.homepage.categories[this.editingHomeCategory.index] = {
        name,
        image: (this.editingHomeCategory.image || '').trim(),
        tagId: String(this.editingHomeCategory.tagId),
        description: (this.editingHomeCategory.description || '').trim(),
        textColor: this.editingHomeCategory.textColor || '#1f1f1f'
      };
      this.editingHomeCategory = null;
    },
    deleteHomeCategory(categoryIndex) {
      if (!this.configData.homepage || !Array.isArray(this.configData.homepage.categories)) return;
      this.configData.homepage.categories.splice(categoryIndex, 1);
    },
    async handleHomeCategoryEditImageUpload(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const url = await this.uploadFile(file);
      if (url) {
        this.editingHomeCategory.image = url;
      }
      e.target.value = '';
    },
    async handleBackgroundImageUpload(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const url = await this.uploadFile(file);
      if (url) {
        if (!this.configData.colors || typeof this.configData.colors !== 'object') {
          this.configData.colors = {};
        }
        this.configData.colors.pageBackgroundImage = url;
      }
      e.target.value = '';
    },
    removeBackgroundImage() {
      if (!this.configData.colors || typeof this.configData.colors !== 'object') {
        this.configData.colors = {};
      }
      this.configData.colors.pageBackgroundImage = '';
    },
    dragStart(e, idx) {
      try {
        e.dataTransfer.setData('text/plain', String(idx));
        e.currentTarget.classList.add('dragging');
      } catch (err) {
        console.warn('dragStart error', err);
      }
    },
    dragEnd(e) {
      try { e.currentTarget.classList.remove('dragging'); } catch (err) { console.warn('dragEnd error', err); }
    },
    dropImage(e, idx, product) {
      try {
        const fromRaw = e.dataTransfer.getData('text/plain');
        const from = parseInt(fromRaw, 10);
        if (isNaN(from) || !product || !Array.isArray(product.images)) return;
        if (from === idx) return;
        const arr = product.images;
        const [item] = arr.splice(from, 1);
        // if dropping to the right of original and index decreased by removal, adjust
        const insertIndex = from < idx ? idx : idx;
        arr.splice(insertIndex, 0, item);
        // keep primary image in sync
        product.image = arr[0] || '';
        this.$forceUpdate && this.$forceUpdate();
      } catch (err) {
        console.error('dropImage error', err);
      }
    },
    moveImage(product, idx, dir) {
      if (!product || !Array.isArray(product.images)) return;
      const to = idx + dir;
      if (to < 0 || to >= product.images.length) return;
      const tmp = product.images[to];
      product.images[to] = product.images[idx];
      product.images[idx] = tmp;
      // keep primary image in sync
      if (product.images.length) product.image = product.images[0];
    },
    removeImage(product, idx) {
      if (!product || !Array.isArray(product.images)) return;
      product.images.splice(idx, 1);
      if (!product.images.length) product.image = '';
      else product.image = product.images[0];
    }
  }
};
</script>

<style scoped>
.admin-panel {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

h1 {
  color: #333;
  border-bottom: 3px solid #8B4513;
  padding-bottom: 10px;
}

/* Tabs */
.admin-tabs {
  display: flex;
  gap: 10px;
  margin: 20px 0;
  border-bottom: 2px solid #ddd;
}

.tab-button {
  padding: 12px 20px;
  border: none;
  background: #f5f5f5;
  cursor: pointer;
  font-size: 16px;
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;
}

.tab-button:hover {
  background: #e0e0e0;
}

.tab-button.active {
  background: white;
  border-bottom-color: #8B4513;
  color: #8B4513;
  font-weight: bold;
}

.tab-content {
  margin-top: 20px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Formular */
.add-product-form {
  background: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #333;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
}

.form-group textarea {
  resize: vertical;
}

/* Buttons */
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.btn-primary {
  background: #8B4513;
  color: white;
}

.btn-primary:hover {
  background: #6B3410;
}

.btn-secondary {
  background: #666;
  color: white;
}

.btn-secondary:hover {
  background: #555;
}

.btn-danger {
  background: #d32f2f;
  color: white;
}

.btn-danger:hover {
  background: #b71c1c;
}

.btn-success {
  background: #43a047;
  color: white;
}

.btn-success:hover {
  background: #2e7d32;
}

/* Produke */
.products-list {
  margin-top: 30px;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 16px;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.product-card {
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.product-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 10px;
}

.product-card h4 {
  margin: 10px 0 5px 0;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 1rem;
}

.product-card .price {
  font-size: 18px;
  font-weight: bold;
  color: #8B4513;
  margin: 5px 0 10px 0;
}

.product-card .description {
  color: #666;
  font-size: 13px;
  margin: 10px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 2.0em;
}

.product-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.admin-product-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.tag-select-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 8px;
  margin-top: 6px;
}

.tag-select-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.tag-preview-pill {
  color: #fff;
  font-weight: 600;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
}

.inline-help {
  margin: 8px 0 0 0;
  color: #666;
  font-size: 13px;
}

.admin-tags-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.admin-tag-row {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.admin-tag-description {
  margin: 0;
  color: #555;
  flex: 1;
}

.admin-tag-actions {
  display: flex;
  gap: 8px;
}

.product-actions .btn {
  flex: 1;
  padding: 8px;
  font-size: 13px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-content h3 {
  margin-top: 0;
  color: #333;
}

/* Image tiles for upload/manage */
.image-tiles {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
}
.image-tiles .tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 120px;
}
.image-tiles .thumb {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border: 1px solid #ddd;
  border-radius: 6px;
}
.image-tiles .tile {
  cursor: grab;
}
.image-tiles .tile.dragging {
  opacity: 0.5;
}
.tile-actions {
  display: flex;
  gap: 6px;
  margin-top: 6px;
}

.modal-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  justify-content: flex-end;
}

.modal-actions .btn {
  flex: 1;
}

.home-category-preview-image {
  display: block;
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 50%;
  margin-top: 8px;
  border: 1px solid #ddd;
  background: #fff;
}

.home-category-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #ececec;
  border-radius: 8px;
  background: #fff;
}

.home-category-preview {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
}

.category-icon-small {
  width: 42px;
  height: 42px;
  object-fit: cover;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

/* Admin Texts & Colors */
.settings-panel-group {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.settings-panel {
  background: #fff;
  border: 1px solid #e9e1d7;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(90, 60, 30, 0.05);
}

.settings-panel-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f7efe7;
  color: #4d2b11;
  border: none;
  padding: 14px 18px;
  font-size: 1rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.settings-panel-body {
  padding: 0 18px 18px;
}

.admin-section {
  background: #f9f9f9;
  padding: 18px;
  border-radius: 8px;
  margin-bottom: 18px;
}

.compact-section {
  margin-bottom: 14px;
}

.admin-section h3 {
  margin-top: 0;
  margin-bottom: 12px;
  color: #333;
  font-size: 1.05rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}

.compact-grid {
  gap: 12px;
}

.color-help {
  margin: -4px 0 14px 0;
  color: #666;
  font-size: 14px;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.color-section {
  background: #fff;
  border: 1px solid #eee;
  padding: 12px;
  border-radius: 6px;
}

.color-section-title {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #444;
}

.color-group {
  display: flex;
  flex-direction: column;
}

.color-group label {
  font-weight: bold;
  margin-bottom: 8px;
  font-size: 13px;
  color: #333;
}

.color-input {
  display: flex;
  gap: 10px;
  align-items: center;
}

.color-input input[type="color"] {
  width: 50px;
  height: 40px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
}

.color-input span {
  font-family: monospace;
  font-size: 12px;
  color: #666;
}

.color-input select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
}

.background-image-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.background-preview {
  width: 100%;
  max-height: 120px;
  object-fit: cover;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.save-bar {
  margin-top: 12px;
}

.btn-large {
  width: 100%;
  padding: 12px;
  font-size: 16px;
}

.message {
  margin-top: 15px;
  padding: 12px;
  border-radius: 4px;
  text-align: center;
}

.message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}
</style>